import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const DeviationDartboard = ({
  xDeviation,
  yDeviation,
  phi = 0,
  maxDeviation = 50,
}) => {
  const [isZoomed, setIsZoomed] = useState(true);
  const { t } = useTranslation();

  const OUTER_RADIUS = 170;
  const TRIPLE_RADIUS = 107;
  const DOUBLE_RADIUS = OUTER_RADIUS;
  const OUTER_BULL_RADIUS = 16;
  const INNER_BULL_RADIUS = 6.35;
  const cx = 0;
  const cy = 0;

  const targetRadius = TRIPLE_RADIUS - 4;
  const scaleFactor = 1;
  const targetX = cx;
  const targetY = cy - targetRadius;

  return (
    <div className="flex flex-col w-full max-w-full">
      <div
        className="relative w-full max-w-full"
        style={{ aspectRatio: "1 / 1" }}
      >
        <button
          onClick={() => setIsZoomed(!isZoomed)}
          className="absolute top-2 right-2 z-10 bg-secondary/90 hover:bg-primary/90 text-white p-2 rounded-full shadow-md transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-400"
          title={isZoomed ? "Zoom Out" : "Zoom In"}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {isZoomed ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM7 10h6m-3-3v6"
              />
            )}
          </svg>
        </button>

        <svg
          className="absolute inset-0 w-full h-full max-w-full"
          viewBox={
            isZoomed
              ? `${-OUTER_RADIUS / 2} ${
                  -OUTER_RADIUS * 1.01
                } ${OUTER_RADIUS} ${OUTER_RADIUS}`
              : `${-OUTER_RADIUS * 1.01} ${-OUTER_RADIUS * 1.01} ${
                  OUTER_RADIUS * 2.03
                } ${OUTER_RADIUS * 2}`
          }
          preserveAspectRatio="xMidYMid meet"
          style={{ maxWidth: "100%", height: "auto" }}
        >
          {/* Grid Lines */}
          <circle
            r={OUTER_RADIUS}
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.2"
            opacity="0.6"
          />
          <circle
            r={DOUBLE_RADIUS - 8}
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.2"
            opacity="0.6"
          />
          <circle
            r={TRIPLE_RADIUS}
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.2"
            opacity="0.6"
          />
          <circle
            r={TRIPLE_RADIUS - 8}
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.2"
            opacity="0.6"
          />

          {/* Radial lines */}
          {Array.from({ length: 20 }, (_, i) => {
            const angle = ((i * 18 - 99) * Math.PI) / 180;
            const x = OUTER_RADIUS * Math.cos(angle);
            const y = OUTER_RADIUS * Math.sin(angle);
            const innerX = OUTER_BULL_RADIUS * Math.cos(angle);
            const innerY = OUTER_BULL_RADIUS * Math.sin(angle);
            return (
              <path
                key={`line-${i}`}
                d={`M ${innerX} ${innerY} L ${x} ${y}`}
                stroke="#9CA3AF"
                strokeWidth="1.2"
                opacity="0.6"
                fill="none"
              />
            );
          })}

          {/* Elliptical Deviation Rings */}
          <ellipse
            cx={targetX}
            cy={targetY}
            rx={Math.abs(xDeviation) * scaleFactor}
            ry={Math.abs(yDeviation) * scaleFactor}
            transform={`rotate(${phi} ${targetX} ${targetY})`}
            fill="none"
            stroke="#ff4d4d"
            strokeWidth="2"
            opacity="0.9"
          />
          <ellipse
            cx={targetX}
            cy={targetY}
            rx={Math.abs(xDeviation) * scaleFactor * 2}
            ry={Math.abs(yDeviation) * scaleFactor * 2}
            transform={`rotate(${phi} ${targetX} ${targetY})`}
            fill="none"
            stroke="#ff4d4d"
            strokeWidth="2"
            opacity="0.3"
          />

          {/* Target marker */}
          <circle cx={targetX} cy={targetY} r="2" fill="#ffa600" />

          {/* Bulls */}
          <circle
            cx={cx}
            cy={cy}
            r={OUTER_BULL_RADIUS}
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.2"
            opacity="0.6"
          />
          <circle
            cx={cx}
            cy={cy}
            r={INNER_BULL_RADIUS}
            fill="none"
            stroke="#9CA3AF"
            strokeWidth="1.2"
            opacity="0.6"
          />
        </svg>
      </div>

      {/* Legend */}
      <div className="p-3 rounded-lg mt-2">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-sm"
              style={{ backgroundColor: "#ff4d4d", opacity: 0.9 }}
            />
            <span className="text-lightGray text-xs">
              {t("statistics.myStatistics.ellipse68")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-sm"
              style={{ backgroundColor: "#ff4d4d", opacity: 0.3 }}
            />
            <span className="text-lightGray text-xs">
              {t("statistics.myStatistics.ellipse95")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviationDartboard;
